import type { ReactNode, Ref } from 'react'
import { useEffect, useImperativeHandle } from 'react'
import { createPortal } from 'react-dom'
import { useHereLocation, useMarker } from '../hooks'
import type { Location } from '../hooks/useHereLocation'
import { useHereMap } from '../hooks/useHereMap'

/** Imperative handle for moving a marker without a React re-render. */
export type MapMarkerHandle = {
  setPosition: (latitude: number, longitude: number) => void
}

type MapMarkerProps = {
  position: Location
  children: ReactNode
  className?: string
  visible?: boolean
  onClick?: (location: Location) => void
  zIndex?: number
  placement?: 'center' | 'bottom-left'
  animatePosition?: boolean
  ref?: Ref<MapMarkerHandle>
}

/**
 * MapMarker puts its children on the map at a specific location.
 * It should be used as a direct child of the `HereMap` component.
 * Uses React portal to render directly into the HERE Maps clone element,
 * so all React updates are reflected immediately without re-cloning.
 *
 * @param position The location of the marker.
 * @param children The actual UI that should be shown.
 * @param className Optional CSS class name applied to a wrapper div inside the marker.
 * @param visible Whether the marker is visible on the map.
 * @param onClick Click handler for the marker.
 * @param zIndex Z-index for marker stacking order.
 * @param placement Marker placement relative to the position point.
 * @param animatePosition Whether to animate position changes.
 * @param ref Imperative handle to move the marker without re-rendering.
 */
export const MapMarker = (props: MapMarkerProps) => {
  const { map } = useHereMap()
  const position = useHereLocation(props.position)
  const { portalTarget, setPosition } = useMarker({
    map,
    position,
    visible: props.visible,
    zIndex: props.zIndex,
    placement: props.placement,
    animatePosition: props.animatePosition,
  })

  useImperativeHandle(props.ref, () => ({ setPosition }), [setPosition])

  // Attach click listener to portal target
  useEffect(() => {
    if (!portalTarget || !props.onClick) return
    const onClick = props.onClick
    const handler = () => onClick(props.position)
    portalTarget.addEventListener('click', handler)
    return () => {
      portalTarget.removeEventListener('click', handler)
    }
  }, [portalTarget, props.onClick, props.position])

  if (!portalTarget) return null

  return createPortal(
    <div className={props.className}>{props.children}</div>,
    portalTarget,
  )
}
